import { type ReactNode, useEffect, useRef, useState } from "react";

import type { MapViewApi } from "@map/mapiq-map-react/src/MapView";
import { createHaNViewStore, HaNViewStoreContext, type HereAndNowTab } from "@lib/logic";

import { useHereAndNowData } from "../data/useHereAndNowData";
import { HaNErrorBoundary } from "../shared/HaNErrorBoundary";
import { haNSession } from "../state/session/haNSession";
import { HaNContext } from "./HaNContext";

type HaNPageProps = {
  tab: HereAndNowTab;
  children?: ReactNode;
};

// On phones the app header is hidden, so the page also takes its row and shows the building there
export const HaNPage = ({ tab, children }: HaNPageProps) => {
  return (
    <div className="h-[calc(var(--100vh,100vh)-60px)] [grid-area:fullbody/fullbleed] max-sm:h-[calc(var(--100vh,100vh)-64px)] max-sm:[grid-area:header-start/fullbleed-start/body-end/fullbleed-end]">
      <HaNErrorBoundary
        area="Page"
        resetKeys={[tab]}>
        <HaNPageContent tab={tab}>{children}</HaNPageContent>
      </HaNErrorBoundary>
    </div>
  );
};

const HaNPageContent = ({ tab, children }: HaNPageProps) => {
  const data = useHereAndNowData(tab);
  const [viewStore] = useState(createHaNViewStore);
  const mapViewApiRef = useRef<MapViewApi>(null);

  // Every way onto a tab counts (tab bar, deep links, back button), so this follows the route instead of clicks
  useEffect(
    function rememberTab() {
      haNSession.rememberTab(tab);
    },
    [tab],
  );

  // Regions place themselves in this grid. On phones `data-mobile-view` lets them switch between map and list in CSS.
  return (
    <HaNContext.Provider value={{ tab, ...data, mapViewApiRef }}>
      <HaNViewStoreContext.Provider value={viewStore}>
        <div
          data-mobile-view={data.mobileView.view}
          className="group/han grid size-full overflow-hidden bg-white grid-cols-[375px_1fr] grid-rows-[auto_auto_auto_1fr] max-sm:grid-cols-1 max-sm:grid-rows-[60px_auto_auto_auto_1fr]">
          {children}
        </div>
      </HaNViewStoreContext.Provider>
    </HaNContext.Provider>
  );
};
